import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DOCTOR_ROUTES } from "@/routes/patientRoutes/routePaths";
import { useDoctorFollow } from "../../../../ProfileDoctor/hooks/useDoctorFollow";
import "./DoctorInfoCard.css";

const PhoneIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M10.833 1.66602C12.8221 1.66602 14.7298 2.45619 16.1363 3.86271C17.5428 5.26924 18.333 7.17689 18.333 9.16602"
      stroke="currentColor"
      strokeWidth="0.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.833 5C11.9381 5 12.9979 5.43899 13.7793 6.22039C14.5607 7.00179 14.9997 8.0616 14.9997 9.16667"
      stroke="currentColor"
      strokeWidth="0.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.527 13.806C11.6991 13.8851 11.893 13.9031 12.0767 13.8572C12.2605 13.8113 12.4231 13.7042 12.5378 13.5535L12.8337 13.166C12.9889 12.959 13.1902 12.791 13.4216 12.6753C13.6531 12.5596 13.9083 12.4993 14.167 12.4993H16.667C17.109 12.4993 17.5329 12.6749 17.8455 12.9875C18.1581 13.3001 18.3337 13.724 18.3337 14.166V16.666C18.3337 17.108 18.1581 17.532 17.8455 17.8445C17.5329 18.1571 17.109 18.3327 16.667 18.3327C12.6887 18.3327 8.87344 16.7523 6.06039 13.9393C3.24734 11.1262 1.66699 7.31093 1.66699 3.33268C1.66699 2.89065 1.84259 2.46673 2.15515 2.15417C2.46771 1.84161 2.89163 1.66602 3.33366 1.66602H5.83366C6.27569 1.66602 6.69961 1.84161 7.01217 2.15417C7.32473 2.46673 7.50033 2.89065 7.50033 3.33268V5.83268C7.50033 6.09142 7.44008 6.34661 7.32437 6.57804C7.20866 6.80946 7.04065 7.01077 6.83366 7.16602L6.44366 7.45852C6.29067 7.57533 6.18284 7.7415 6.13848 7.92881C6.09413 8.11611 6.11598 8.313 6.20033 8.48602C7.33923 10.7992 9.21235 12.67 11.527 13.806Z"
      stroke="currentColor"
      strokeWidth="0.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LocationIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M16.6663 8.33268C16.6663 12.4935 12.0505 16.8268 10.5005 18.1652C10.3561 18.2738 10.1803 18.3325 9.99967 18.3325C9.81901 18.3325 9.64324 18.2738 9.49884 18.1652C7.94884 16.8268 3.33301 12.4935 3.33301 8.33268C3.33301 6.56457 4.03539 4.86888 5.28563 3.61864C6.53587 2.36839 8.23156 1.66602 9.99967 1.66602C11.7678 1.66602 13.4635 2.36839 14.7137 3.61864C15.964 4.86888 16.6663 6.56457 16.6663 8.33268Z"
      stroke="currentColor"
      strokeWidth="0.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 10.834C11.3807 10.834 12.5 9.7147 12.5 8.33398C12.5 6.95327 11.3807 5.83398 10 5.83398C8.61929 5.83398 7.5 6.95327 7.5 8.33398C7.5 9.7147 8.61929 10.834 10 10.834Z"
      stroke="currentColor"
      strokeWidth="0.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M8 2V6M16 2V6M3 10H21M5 4H19A2 2 0 0 1 21 6V20A2 2 0 0 1 19 22H5A2 2 0 0 1 3 20V6A2 2 0 0 1 5 4Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ExternalIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M12 8V12M12 16H12.01"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const DoctorInfoCard = ({
  doctor,
  onClose,
  showClose = true,
  isMedilinker = false,
  onAppointmentClick,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    isFollowing,
    toggleFollow,
    canFollow,
    loading: followLoading,
  } = useDoctorFollow(isMedilinker ? doctor : null);

  if (!doctor) return null;

  const handleBook = (e) => {
    e.stopPropagation();
    if (typeof onAppointmentClick === "function") {
      onAppointmentClick(doctor);
    }
  };

  const handleOpenProfile = (e) => {
    e.stopPropagation();
    if (!isMedilinker) return;

    const id = doctor.medilinkerAccountId || doctor.id;
    if (!id) return;

    navigate(
      DOCTOR_ROUTES.PROFILE_DOCTOR(String(id).replace(/^DR_/, ""))
    );
  };

  const handleToggleFollow = (e) => {
    e.stopPropagation();
    if (!followLoading) {
      toggleFollow();
    }
  };

  return (
    <div
      className={`doctor-floating-card ${
        isMedilinker ? "dfc--medilinker" : "dfc--external"
      }`}
    >
      {showClose && (
        <button
          type="button"
          className="dfc-close-btn"
          onClick={onClose}
          title={t("common.close", "Close")}
          aria-label={t("common.close", "Close")}
        >
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}

      <div className="dfc-topbar">
        <span
          className={`dfc-status-badge ${
            isMedilinker
              ? "dfc-status-badge--medilinker"
              : "dfc-status-badge--external"
          }`}
        >
          <span className="dfc-status-dot" />
          {isMedilinker
            ? t("doctorFinder.card.medilinkDoctor", "MediLink doctor")
            : t("doctorFinder.card.externalDoctor", "External doctor")}
        </span>
      </div>

      <div className="dfc-header">
        <div
          className={`dfc-avatar ${
            isMedilinker ? "dfc-avatar--medilinker" : ""
          }`}
        >
          <img
            src={doctor.avatar || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
            alt={doctor.name || "Doctor"}
            onError={(e) => {
              e.currentTarget.src =
                "https://cdn-icons-png.flaticon.com/512/149/149071.png";
            }}
          />
        </div>

        <div className="dfc-main">
          {isMedilinker ? (
            <button
              type="button"
              className="dfc-name-btn"
              onClick={handleOpenProfile}
            >
              <h3 className="dfc-name">{doctor.name}</h3>
            </button>
          ) : (
            <h3 className="dfc-name">{doctor.name}</h3>
          )}

          <p className="dfc-specialty">{doctor.specialty}</p>

          {doctor.distanceText && (
            <p className="dfc-distance">
              {doctor.distanceText}
            </p>
          )}
        </div>
      </div>

      <div className="dfc-body">
        {doctor.phone && (
          <div className="dfc-row">
            <span className="dfc-icon">
              <PhoneIcon />
            </span>
            <span className="dfc-text">{doctor.phone}</span>
          </div>
        )}

        {doctor.address && (
          <div className="dfc-row">
            <span className="dfc-icon">
              <LocationIcon />
            </span>
            <span className="dfc-text">{doctor.address}</span>
          </div>
        )}
      </div>

      <div className="dfc-footer">
        <div className="dfc-footer__actions">
          <button
            type="button"
            className={`dfc-book-btn ${
              isMedilinker
                ? "dfc-book-btn--medilinker"
                : "dfc-book-btn--external"
            }`}
            onClick={handleBook}
          >
            {isMedilinker ? (
              <>
                <CalendarIcon />
                {t(
                  "doctorFinder.card.bookAppointment",
                  "Book Appointment"
                )}
              </>
            ) : (
              <>
                <ExternalIcon />
                {t(
                  "doctorFinder.card.notOnMedilink",
                  "Not on MediLink"
                )}
              </>
            )}
          </button>

          {isMedilinker && canFollow && (
            <button
              type="button"
              className={`dfc-follow-btn ${
                isFollowing ? "dfc-follow-btn--following" : ""
              }`}
              onClick={handleToggleFollow}
              disabled={followLoading}
            >
              {followLoading
                ? t("common.loading", "Loading...")
                : isFollowing
                ? t("doctorFinder.card.unfollow", "Following")
                : t("doctorFinder.card.follow", "Follow")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DoctorInfoCard;